import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  approveGbpPost,
  approveGbpQa,
  approveGbpReview,
  createGbpPost,
  draftGbpQaResponse,
  draftGbpReviewResponse,
  editGbpReviewResponse,
  fetchGbp,
} from '../api';

export function useGbp(page = 1) {
  return useGrowthQuery({
    queryKey: [...growthKeys.gbp.overview(), page] as const,
    queryFn: () => fetchGbp(page),
  });
}

export function useGbpMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'gbp'] });

  const approveReview = useMutation({
    mutationFn: approveGbpReview,
    onSuccess: () => {
      notify.success('Review response approved');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const approveQa = useMutation({
    mutationFn: approveGbpQa,
    onSuccess: () => {
      notify.success('Q&A response approved');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const draftReview = useMutation({
    mutationFn: ({ id, responseTone }: { id: string; responseTone?: string }) =>
      draftGbpReviewResponse(id, responseTone ? { responseTone } : {}),
    onSuccess: () => {
      notify.success('Review draft queued');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const editReview = useMutation({
    mutationFn: ({ id, responseText }: { id: string; responseText: string }) =>
      editGbpReviewResponse(id, responseText),
    onSuccess: () => {
      notify.success('Review response updated');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const createPost = useMutation({
    mutationFn: createGbpPost,
    onSuccess: () => {
      notify.success('GBP post queued');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const approvePost = useMutation({
    mutationFn: approveGbpPost,
    onSuccess: () => {
      notify.success('GBP post approved');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const draftQa = useMutation({
    mutationFn: draftGbpQaResponse,
    onSuccess: () => {
      notify.success('Q&A draft queued');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  return {
    approveReview,
    approveQa,
    draftReview,
    editReview,
    createPost,
    approvePost,
    draftQa,
  };
}
